import * as React from 'react';

import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import PasteProductsDialog from 'components/ReportRequestV2/PasteProductsDialog';
import { uniq } from 'lodash';

import { StepProps } from './types';
import { ProductCatalog } from '../hooks/useProductCatalog';
import { setField } from '../model/reducer';

interface Props extends StepProps {
  catalog: ProductCatalog;
}

/**
 * Optional named custom class. Products come from a pasted list only (names
 * that don't match are repaired in the paste dialog), and show as chips.
 */
const CustomClassSection: React.FC<Props> = ({
  state,
  dispatch,
  errors,
  catalog,
}) => {
  const [pasteOpen, setPasteOpen] = React.useState(false);
  const products = state.customClassDbProducts;
  const added = React.useMemo(() => new Set(products), [products]);
  const setProducts = (names: string[]) =>
    dispatch(setField('customClassDbProducts', names));

  return (
    <Accordion variant="outlined" disableGutters defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="subtitle1">Custom class database</Typography>
          <Typography variant="body2" color="text.secondary">
            Optional. Groups products into a named custom class.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'grid', gap: 2, maxWidth: 720 }}>
          <TextField
            label="Class database name"
            value={state.customClassDbName}
            onChange={(e) =>
              dispatch(setField('customClassDbName', e.target.value))
            }
            error={!!errors.customClassDb && !state.customClassDbName.trim()}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ContentPasteIcon />}
              onClick={() => setPasteOpen(true)}
              disabled={catalog.loading}
              aria-label="Paste class database products"
            >
              Paste list
            </Button>
            <Typography
              variant="body2"
              color="text.secondary"
              aria-live="polite"
            >
              {products.length === 0
                ? 'No products yet.'
                : `${products.length} product${products.length === 1 ? '' : 's'}`}
            </Typography>
            {products.length > 0 && (
              <Button size="small" onClick={() => setProducts([])}>
                Clear all
              </Button>
            )}
          </Box>
          {products.length > 0 && (
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
              role="list"
              aria-label="Class database products"
            >
              {products.map((name) => (
                <Chip
                  key={name}
                  role="listitem"
                  size="small"
                  label={name}
                  onDelete={() =>
                    setProducts(products.filter((p) => p !== name))
                  }
                />
              ))}
            </Box>
          )}
          {errors.customClassDb && (
            <FormHelperText error>{errors.customClassDb}</FormHelperText>
          )}
        </Box>
        <PasteProductsDialog
          open={pasteOpen}
          onClose={() => setPasteOpen(false)}
          catalog={catalog.catalog}
          description="One product per line, e.g. copied from a spreadsheet column. Names must match the catalog exactly; case only matters when two products differ by it. Repair any that don't: the repaired name replaces what you pasted and is highlighted."
          onAdd={(rows) =>
            setProducts(uniq([...products, ...rows.map((p) => p.name)]))
          }
          addedNames={added}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default CustomClassSection;
